import { useAuth } from '@/hooks/useAuth';
import {
  Loader2, UserCog, Users, FileSignature, Megaphone, Newspaper, Bot, Calendar,
  Landmark, BookOpen, Shield, MessageSquare, Map, Settings2, Inbox, Layers, Music, ShoppingBag, Dice5,
  ShieldAlert, Church, CloudSun, Activity, Radar, Sparkles, Images, Gem, BookImage, Disc3,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { ApplicationList } from '@/components/jartinet/ApplicationList';
import { OnboardingSettings } from '@/components/jartinet/admin/OnboardingSettings';
import { TransitionLink } from '@/providers/TransitionProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';

type Section = { title: string; icon: ReactNode; href: string; color: string };

const adminSections: Section[] = [
  { title: 'Usuarios', icon: <UserCog />, href: '/jartinet/admin/usuarios', color: '#f43f5e' },
  { title: 'Hermandades', icon: <Shield />, href: '/jartinet/admin/hermandades', color: '#f59e0b' },
  { title: 'Bandas', icon: <Music />, href: '/jartinet/admin/bandas', color: '#38bdf8' },
  { title: 'Social', icon: <Users />, href: '/jartinet/admin/social', color: '#ec4899' },
  { title: 'Contratos', icon: <FileSignature />, href: '/jartinet/admin/contratos', color: '#38bdf8' },
  { title: 'Avisos', icon: <Megaphone />, href: '/jartinet/admin/avisos', color: '#fb923c' },
  { title: 'Noticias', icon: <Newspaper />, href: '/jartinet/admin/noticias', color: '#a78bfa' },
  { title: 'Mod Cofrade', icon: <Bot />, href: '/jartinet/admin/mod', color: '#34d399' },
  { title: 'Periódico', icon: <BookOpen />, href: '/jartinet/admin/periodico', color: '#60a5fa' },
  { title: 'Calendario', icon: <Calendar />, href: '/jartinet/admin/calendario', color: '#2dd4bf' },
  { title: 'Mapa de Ciudad', icon: <Map />, href: '/jartinet/admin/mapa', color: '#a3e635' },
  { title: 'Banco', icon: <Landmark />, href: '/jartinet/admin/banco', color: '#fbbf24' },
  { title: 'Programa S. Santa', icon: <Newspaper />, href: '/jartinet/admin/programa-ss26', color: '#f87171' },
  { title: 'Discord Bot', icon: <MessageSquare />, href: '/jartinet/admin/discord', color: '#818cf8' },
  { title: 'Arzobispado', icon: <Landmark />, href: '/jartinet/admin/arzobispado', color: '#c084fc' },
  { title: 'Organizaciones', icon: <Landmark />, href: '/jartinet/admin/organizaciones', color: '#a855f7' },
  { title: 'JartiCards', icon: <Layers />, href: '/jartinet/admin/jarticards', color: '#f472b6' },
  { title: 'Personalizables', icon: <Gem />, href: '/jartinet/admin/personalizables', color: '#34d399' },
  { title: 'JartiBet', icon: <Dice5 />, href: '/jartinet/admin/jartibet', color: '#fbbf24' },
  { title: 'Seguridad Mod', icon: <ShieldAlert />, href: '/jartinet/admin/seguridad-mod', color: '#f43f5e' },
  { title: 'Seguridad Roblox', icon: <ShieldAlert />, href: '/jartinet/admin/seguridad-roblox', color: '#6366f1' },
  { title: 'Mapa Servidor', icon: <Map />, href: '/jartinet/admin/mapa-servidor', color: '#34d399' },
  { title: 'Iglesias', icon: <Church />, href: '/jartinet/admin/iglesias', color: '#f59e0b' },
  { title: 'Tiempo', icon: <CloudSun />, href: '/jartinet/admin/tiempo', color: '#38bdf8' },
  { title: 'Actividad', icon: <Activity />, href: '/jartinet/admin/actividad', color: '#10b981' },
  { title: 'CECOP', icon: <Radar />, href: '/jartinet/admin/cecop', color: '#fb7185' },
  { title: 'El Jarti Inglés', icon: <ShoppingBag />, href: '/jartinet/admin/jartiingles', color: '#ff9900' },
  { title: 'Shaders', icon: <Sparkles />, href: '/jartinet/admin/shaders', color: '#a78bfa' },
  { title: 'Galería', icon: <Images />, href: '/jartinet/admin/galeria', color: '#ec4899' },
  { title: 'Estampitas', icon: <BookImage />, href: '/jartinet/admin/estampitas', color: '#c084fc' },
  { title: 'JartiMarchas', icon: <Disc3 />, href: '/jartinet/admin/jartimarchas', color: '#818cf8' },
];

function hexToRgba(hex: string, alpha: number) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function AdminHomePage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') {
      navigate('/jartinet', { replace: true });
    }
  }, [user, userData, loading, navigate]);

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-10 pb-12">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-primary/15 via-card/40 to-card/20 p-8 md:p-10"
      >
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="relative flex items-center gap-5">
          <div className="relative">
            <div className="absolute inset-0 rounded-2xl bg-primary/40 blur-xl" />
            <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-primary/30 bg-primary/15 text-primary">
              <UserCog className="h-9 w-9" />
            </div>
          </div>
          <div>
            <h1 className="font-headline text-4xl font-bold text-foreground md:text-5xl">
              Panel de Administración
            </h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Gestiona el contenido, las solicitudes y los parámetros del servidor de Jartiland.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Quick access grid */}
      <section className="space-y-4">
        <h2 className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground/70">
          Accesos directos
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {adminSections.map((section, i) => (
            <TransitionLink to={section.href} key={section.href} className="block hover:no-underline">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03, duration: 0.35 }}
                whileHover={{ y: -6 }}
                className="group relative h-full overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-5 transition-colors"
                style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}
              >
                <div
                  className="absolute inset-x-0 top-0 h-[3px] opacity-70"
                  style={{ background: `linear-gradient(90deg, ${section.color}, transparent)` }}
                />
                <div
                  className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
                  style={{ background: hexToRgba(section.color, 0.35) }}
                />
                <div className="relative flex flex-col items-start gap-3">
                  <div
                    className="grid h-11 w-11 place-items-center rounded-xl border transition-transform group-hover:scale-110"
                    style={{
                      color: section.color,
                      borderColor: hexToRgba(section.color, 0.3),
                      background: hexToRgba(section.color, 0.12),
                    }}
                  >
                    {section.icon}
                  </div>
                  <p className="text-sm font-semibold text-foreground">{section.title}</p>
                </div>
              </motion.div>
            </TransitionLink>
          ))}
        </div>
      </section>

      {/* Solicitudes */}
      <section className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl border border-primary/30 bg-primary/12 text-primary">
              <Inbox className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-headline text-2xl font-bold text-foreground">Gestión de Solicitudes</h2>
              <p className="text-sm text-muted-foreground">Acepta o rechaza las solicitudes de acceso al servidor.</p>
            </div>
          </div>

          <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Settings2 className="h-4 w-4" />
                Configurar mensajes
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Mensajes de onboarding</DialogTitle>
                <DialogDescription>
                  Personaliza el correo de credenciales y los mensajes de Discord que se envían al aceptar una solicitud.
                </DialogDescription>
              </DialogHeader>
              <OnboardingSettings />
            </DialogContent>
          </Dialog>
        </div>

        <div className="rounded-2xl border border-white/8 bg-card/30 p-4 md:p-6">
          <ApplicationList />
        </div>
      </section>
    </div>
  );
}
